// "How would you like to pay?" — the whole checkout, for now.
//
// No payment provider is wired (ADR-0009). This records what the customer wants
// and how they intend to send the money; an operator confirms it arrived and
// applies the plan. It is deliberately the same shape a real checkout would
// have, so replacing it later changes this file and nothing else.

import { useEffect, useRef, useState } from "react";
import { formatMoney, priceFor } from "../lib/entitlements";
import { listPaymentMethods, requestPlanChange, type PlanRequestResult, type PaymentMethod } from "../lib/billing";
import { logError } from "../lib/logger";
import type { BillingInterval, PricingCatalog } from "../lib/types";

export function PlanRequestForm({
  workspaceId, catalog, planCode, interval, regionCode, onClose, onDone,
}: {
  workspaceId: string;
  catalog: PricingCatalog;
  planCode: string;
  interval: BillingInterval;
  regionCode: string;
  onClose: () => void;
  onDone: (result: PlanRequestResult) => void | Promise<void>;
}) {
  const submitting = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [method, setMethod] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const plan = catalog.plans.find((p) => p.code === planCode);
  const price = priceFor(catalog, planCode, regionCode, interval);
  const chosen = methods.find((m) => m.code === method) ?? null;

  useEffect(() => {
    listPaymentMethods(regionCode)
      .then((m) => {
        setLoaded(true);
        setMethods(m);
        setMethod((current) => current ?? m[0]?.code ?? null);
      })
      .catch((e) => setError(e.message ?? String(e)));
  }, [regionCode]);

  async function submit() {
    if (submitting.current || !method) return;
    submitting.current = true;
    setSending(true);
    setError(null);
    try {
      const request = await requestPlanChange({
        workspaceId,
        planCode,
        interval,
        regionCode,
        paymentMethod: method,
        contactEmail: email || null,
        contactPhone: phone || null,
        note: note || null,
      });
      await onDone(request);
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("app", e, {
        step: "request_plan_change", workspaceId,
        context: { plan: planCode, interval, method },
      });
    } finally {
      submitting.current = false;
      setSending(false);
    }
  }

  const period = interval === "lifetime" ? "one-time"
    : interval === "year" ? "per year" : "per month";

  return (
    <form className="card plan-request-form" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
      <h2 id="upgrade-title">Upgrade to {plan?.name ?? planCode}</h2>

      <p className="modal-price">
        {price
          ? <>
              <strong>{formatMoney(price.amount_minor, price.currency)}</strong> {period}
            </>
          : "Price on request"}
      </p>

      {/* Said before they commit, not after: nobody is charged by this form. */}
      <p className="muted modal-lede">
        Card payments are not live yet. Tell us how you would like to pay and we will send
        instructions — your plan is applied once the payment reaches us. Nothing is charged now.
      </p>

      <fieldset className="methods" disabled={sending}>
        <legend>How would you like to pay?</legend>
        {!loaded && !error && <p className="muted">Loading payment methods…</p>}
        {loaded && methods.length === 0 && <p className="notice">No payment methods are available for this region. Please contact support.</p>}
        {methods.map((m) => (
          <label key={m.code} className={m.code === method ? "method on" : "method"}>
            <input
              type="radio"
              name="payment-method"
              value={m.code}
              checked={m.code === method}
              onChange={() => setMethod(m.code)}
            />
            <span>
              <strong>{m.name}</strong>
              {m.description && <span className="muted"> — {m.description}</span>}
            </span>
          </label>
        ))}
      </fieldset>

      {chosen?.instructions && <p className="notice">{chosen.instructions}</p>}

      <label htmlFor="upgrade-email">Contact email</label>
      <input
        id="upgrade-email"
        type="email"
        value={email}
        placeholder="Leave blank to use your account email"
        onChange={(e) => setEmail(e.target.value)}
      />

      <label htmlFor="upgrade-phone">Phone or WhatsApp (optional)</label>
      <input id="upgrade-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />

      <label htmlFor="upgrade-note">Anything we should know? (optional)</label>
      <textarea
        id="upgrade-note"
        value={note}
        rows={3}
        placeholder="Billing name, GST/VAT number, purchase order…"
        onChange={(e) => setNote(e.target.value)}
      />

      {error && <div role="alert" className="err modal-error">{error}</div>}

      <div className="row modal-actions">
        <button className="primary" type="submit" disabled={sending || !method}>
          {sending ? "Sending…" : "Send Request"}
        </button>
        <button type="button" onClick={onClose} disabled={sending}>Cancel</button>
      </div>
    </form>
  );
}
